import { invariant } from "@olenbetong/appframe-core";

import { type IClient, type LoginOptions, setDefaultClient } from "../Client.js";
import { requestTimeout } from "../utils/index.js";

export class Client implements IClient {
	private __hostname = "";
	get hostname() {
		return this.__hostname;
	}

	constructor(hostname?: string) {
		this.setHostname(hostname);
	}

	getRequestUrl(path: string) {
		let newHost = this.__hostname;

		if (!this.__hostname && typeof window === "undefined") {
			// We are running in a browser environment, so hostname is not necessary.
			return path;
		}

		if (path.startsWith("http")) {
			return path;
		}

		let newPath = path;
		if (newPath.startsWith("/")) {
			newPath = newPath.substring(1, newPath.length);
		}

		return [newHost, newPath].join("/");
	}

	/**
	 * Wraps fetch and adds JSON headers (Accept and Content-Type) and X-Requested-With
	 * which is required by some handlers. If timeout is set in `init`, the request will
	 * be aborted if not finished by the amount of milliseconds set.
	 *
	 * If Content-Type should not be JSON, either pass in another Content-Type, or set
	 * isJson to false in `init`
	 */
	async afFetch(url: string, init: RequestInit & { timeout?: number; isJson?: boolean }): Promise<Response> {
		let requestInit = Object.assign({}, init);
		let { timeout = 0, isJson = true } = init;
		let controller = new AbortController();

		requestInit.timeout = undefined;
		requestInit.isJson = undefined;

		if (requestInit.signal) {
			requestInit.signal.addEventListener("abort", () => {
				controller.abort();
			});
		} else {
			requestInit.signal = controller.signal;
		}

		if (!requestInit.method) {
			requestInit.method = "POST";
		}

		let afHeaders: HeadersInit = {
			Accept: (init?.headers as any)?.["Accept"] ?? "application/json, text/javascript, */*; q=0.01",
			"X-Requested-With": "XMLHttpRequest",
		};

		if (isJson) {
			afHeaders["Content-Type"] = (init?.headers as any)?.["Content-Type"] ?? "application/json; charset=utf-8";
		}

		requestInit.headers = new Headers({ ...requestInit.headers, ...afHeaders });

		let cancelTimeout = controller && timeout > 0 ? requestTimeout(controller, timeout) : () => null;

		let response = await this.fetch(url, requestInit);
		cancelTimeout();

		if (controller?.signal?.aborted) {
			throw new DOMException("Request has been aborted", "AbortError");
		}

		return response;
	}

	async fetch(input: any, init: any = {}) {
		let url = typeof input === "string" ? input : input.url;
		url = this.getRequestUrl(url);

		invariant(
			url.startsWith("http") || typeof window !== "undefined",
			"Hostname has not been set in NodeFetch. Use setDefaultClient() to configure a default client with hostname.",
		);

		if (url.startsWith("http")) {
			init.credentials = "include";
			init.mode = "cors";
		}

		if (typeof input === "string") {
			input = url;
		} else {
			input = new Request(url, input);
		}

		return await fetch(input, init);
	}

	async login(username: string, password: string, options?: LoginOptions) {
		let response = await this.afFetch("/login", {
			method: "POST",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				username,
				password,
				remember: options?.remember ?? false,
			}),
		});

		if (response.ok) {
			let json = await response.json();
			if (json.success) {
				return json.success;
			} else if (json.error) {
				throw Error(json.error);
			}
			return true;
		} else {
			throw Error(`Login failed: ${response.status} ${response.statusText}`);
		}
	}

	setHostname(hostname?: string) {
		if (hostname) {
			if (!hostname.startsWith("http")) {
				hostname = `https://${hostname}`;
			}

			if (hostname.endsWith("/")) {
				hostname = hostname.substring(0, hostname.length - 1);
			}

			this.__hostname = hostname;
		}
	}
}

// For unknown reasons, Vite still loads this file even if it doesn't use it, and so we
// still need to make sure window exists before setting a default client.
if (typeof window !== "undefined") {
	setDefaultClient(new Client());
}
